/**
 * catalog-row-api — 카탈로그 확정 행 수정(백엔드 /api/admin/catalog-rows, V장 "카탈로그 확정 행 수정" 2026-10-20).
 * GET 행 + 열 메타(잠김) + 변경 이력 · PUT 바뀐 열(잠긴 열은 400). 드로어 [수정] 이 쓴다.
 */
import { z } from 'zod';
import { getJson, postJson, putJson } from './http';
import { E_CATALOG_TABLES } from './catalog-request-api';
import type { ECatalogTable } from './catalog-request-api';

const BASE = '/api/admin/catalog-rows';

export const CatalogRowColumnSchema = z.object({
  name: z.string(),
  comment: z.string().nullable(),
  data_type: z.string(),
  value: z.unknown(),
  locked: z.boolean(),
});
export type CatalogRowColumn = z.infer<typeof CatalogRowColumnSchema>;

export const CatalogRowEventChangeSchema = z.object({ column: z.string(), label: z.string(), old: z.unknown(), new: z.unknown() });
export const CatalogRowEventSchema = z.object({
  at: z.string(),
  who: z.string().nullable(),
  type: z.string(),
  reason: z.string().nullable(),
  changes: z.array(CatalogRowEventChangeSchema),
});
export type CatalogRowEvent = z.infer<typeof CatalogRowEventSchema>;

export const CatalogRowViewSchema = z.object({
  table: z.string(),
  id: z.number().int(),
  option_part: z.string().nullable(),
  columns: z.array(CatalogRowColumnSchema),
  events: z.array(CatalogRowEventSchema),
});
export type CatalogRowView = z.infer<typeof CatalogRowViewSchema>;

export async function fetchCatalogRowView(table: ECatalogTable, id: number): Promise<CatalogRowView> {
  return getJson(`${BASE}/${encodeURIComponent(table)}/${id}`, { schema: CatalogRowViewSchema });
}

/** 바뀐 열만 보낸다(키 = 열 이름). 잠긴 열 · 없는 열 · 빈 사유는 400. 사유는 catalog_event.reason_note · 슬랙 알림(2026-10-27) */
export async function updateCatalogRowView(table: ECatalogTable, id: number, columns: Record<string, unknown>, reasonNote: string): Promise<CatalogRowView> {
  return putJson(`${BASE}/${encodeURIComponent(table)}/${id}`, { columns, reason_note: reasonNote }, CatalogRowViewSchema);
}

/** 카테고리 옮기기 입력 — 새 표 · 폼에서 채운 새 표 칸 · 모델 칸(cpu · gpu · hba · nic 만, 아니면 null). 옛 행의 공통 열은 서버가 옮긴다 */
export type CatalogRowMoveInput = { table: ECatalogTable; columns: Record<string, unknown>; model_columns: Record<string, unknown> | null; carried: Record<string, string>; reason_note: string };   // reason_note: 왜 옮겼나 한 줄 — 필수(빈 글 400, 2026-10-27)   // carried: 옛 표에만 있던 열 → 새 표의 열(사람이 고른 매핑). 안 고른 값은 서버가 specs · 메모에 글로

/** 옮길 수 있는 표 — 부품 표 16(chassis 는 베이 · 모델 FK 가 달려 안 오간다) */
export const MOVABLE_TABLES: readonly ECatalogTable[] = E_CATALOG_TABLES.filter((table) => table !== 'chassis');

/** POST …/:table/:id/move → 옮긴 뒤의 새 행(새 표 · 새 id). chassis · 같은 표 · 필수 빈 칸 400, 새 표에 같은 PN 409 — 메시지로 */
export async function moveCatalogRowView(table: ECatalogTable, id: number, input: CatalogRowMoveInput): Promise<CatalogRowView> {
  return postJson(`${BASE}/${encodeURIComponent(table)}/${id}/move`, input, CatalogRowViewSchema);
}
